import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useDepartmentStaff } from "../../hooks/useDepartmentStaff";
import { teamStatsQueryOptions } from "../../hooks/useTeamStats";
import { isNurseActiveInDepartment } from "../../utils/formatters";
import type { Nurse, TeamStatsResponse } from "../../utils/models";
import { isAgencyNurse } from "../../utils/utilFunctions";

const MONTHLY_ACTIVE_DAYS = 30;

export type StaffAppAccountStats = {
	/** Active staff who opened the staff app in the past 30 days. */
	monthlyActive: number;
	/** Active staff who worked in the past month or are scheduled in the next one. */
	activeInPastOrNextMonth: number;
	/** Staff (agency excluded) who have ever opened the staff app. */
	onBoarded: number;
};

export const buildStaffAppAccountStats = (
	roster: Pick<Nurse, "id" | "lastLoginTime" | "employmentType" | "departmentStatuses">[],
	stats: Pick<TeamStatsResponse, "workedLastMonthNurseIds" | "worksNextMonthNurseIds">,
	departmentId: string,
	now: DateTime,
): StaffAppAccountStats => {
	const activeStaff = roster.filter((nurse) => isNurseActiveInDepartment(nurse, departmentId));
	const monthlyActiveSince = now.minus({ days: MONTHLY_ACTIVE_DAYS });
	const workedOrScheduled = new Set([...stats.workedLastMonthNurseIds, ...stats.worksNextMonthNurseIds]);
	return {
		monthlyActive: activeStaff.filter((nurse) => nurse.lastLoginTime && nurse.lastLoginTime > monthlyActiveSince).length,
		activeInPastOrNextMonth: activeStaff.filter((nurse) => workedOrScheduled.has(nurse.id)).length,
		onBoarded: roster.filter((nurse) => !isAgencyNurse(nurse.employmentType) && !!nurse.lastLoginTime).length,
	};
};

/**
 * The Staff App Accounts card's figures for the department Management Center shows — the drilled department in the
 * ops backoffice, not the one chosen in the header. Undefined until both the roster and the team stats load.
 */
export const useStaffAppAccountStats = (departmentId: string): StaffAppAccountStats | undefined => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { trackError } = useAnalytics();
	const { staff: roster, isError: isRosterError } = useDepartmentStaff(departmentId);
	const { data: stats, isError: isStatsError } = useQuery<TeamStatsResponse>(teamStatsQueryOptions(departmentId));
	const isError = isRosterError || isStatsError;

	useEffect(() => {
		if (!isError) return;
		trackError("management_center_staff_app_stats_fetch_error", { department_id: departmentId });
		showSnackbar(t("management.center.staff.app.load_failed"), "error");
	}, [isError, departmentId, trackError, showSnackbar, t]);

	return useMemo(
		() => (roster && stats && !isError ? buildStaffAppAccountStats(roster, stats, departmentId, DateTime.now()) : undefined),
		[roster, stats, isError, departmentId],
	);
};
